import type { RtcPayload, RTCIceServerLike } from '@rabbitfuel/shared';

type SignalFn = (to: string, data: RtcPayload) => void;
export type PeerState = RTCPeerConnectionState;

const pcConfig = (iceServers: RTCIceServerLike[]): RTCConfiguration => ({
  iceServers: iceServers as RTCIceServer[],
  bundlePolicy: 'max-bundle',
});

/**
 * Nimmt den Drehvermerk (`urn:3gpp:video-orientation`) aus der Beschreibung
 * der Gegenstelle.
 *
 * Ein hochkant gehaltenes iPhone schickt seine Bilder sonst liegend und haengt
 * nur an, dass sie um 90 Grad zu drehen sind. Safari am Mac dreht zwar,
 * bemisst das Bild aber falsch: es landet um den Faktor 9/16 zu klein in der
 * Mitte des Rahmens -- bis das Handy einmal quer und zurueck gedreht wird.
 * Ist der Vermerk nicht ausgehandelt, richtet der Sender die Bilder selbst
 * auf, und jeder Empfaenger bekommt ein gewoehnliches Hochkant-Bild -- auch
 * der Renderer fuer den RTMP-Ausgang.
 *
 * Gestrichen wird auf *beiden* Seiten und jeweils in der fremden
 * Beschreibung: so greift es, auch wenn nur eine Seite schon den neuen Stand
 * geladen hat, und keine Seite muss ihre eigene Beschreibung umschreiben.
 */
const withoutRotationMark = (sdp: string | undefined) =>
  sdp?.replace(/^a=extmap:\d+(?:\/\w+)? urn:3gpp:video-orientation\r?\n/gm, '');

/**
 * Serialisiert die Signalverarbeitung pro Gegenstelle.
 *
 * Ohne das koennen Antwort und ICE-Kandidaten gleichzeitig verarbeitet werden:
 * ein Kandidat, der vor `setRemoteDescription` ankommt, wird von
 * `addIceCandidate` abgelehnt und die Verbindung findet nie zusammen.
 */
class SignalQueue {
  private chains = new Map<string, Promise<void>>();

  run(key: string, task: () => Promise<void>) {
    const next = (this.chains.get(key) ?? Promise.resolve())
      .then(task)
      .catch((err) => console.warn('[rtc] Signalfehler', key, err));
    this.chains.set(key, next);
    return next;
  }

  drop(key: string) {
    this.chains.delete(key);
  }
}

/**
 * Sendeseite: haelt pro Zuschauer eine eigene PeerConnection.
 *
 * Das ist ein Mesh -- gut fuer ein POC mit wenigen Gegenstellen (Regie +
 * Hauptbild), aber der Upload des Handys skaliert nicht auf hunderte
 * Zuschauer. Der Schritt danach ist ein SFU, siehe docs/ARCHITECTURE.md.
 */
export class Publisher {
  private peers = new Map<string, RTCPeerConnection>();
  private pendingIce = new Map<string, RTCIceCandidateInit[]>();
  private queue = new SignalQueue();
  private stream: MediaStream | null = null;
  /** Hoehe, ueber die das Livebild nicht hinausgeht -- siehe `limitVideo`. */
  private maxVideoHeight: number | null = null;
  private fitTimer: number | null = null;
  private appliedScale = 1;

  /**
   * `sourceId` steht in jedem Signal, das hier hinausgeht -- und jedes, das
   * hereinkommt, muss sie tragen. Grund: ein Tab kann zwei Quellen senden,
   * und ein Zuschauer kann beide abonnieren. Dann gibt es zwischen denselben
   * zwei Verbindungen zwei Aushandlungen, die nur die Quelle unterscheidet.
   */
  constructor(
    private signal: SignalFn,
    private iceServers: RTCIceServerLike[],
    private sourceId: string,
    private onPeerState?: (subscriberId: string, state: PeerState) => void,
  ) {}

  setStream(stream: MediaStream | null) {
    this.stream = stream;
    if (!stream) {
      this.closeAll();
      return;
    }
    // Laufende Verbindungen auf die neuen Tracks umziehen (Kamerawechsel).
    for (const pc of this.peers.values()) {
      for (const sender of pc.getSenders()) {
        const track = stream.getTracks().find((t) => t.kind === sender.track?.kind);
        if (track && track !== sender.track) void sender.replaceTrack(track);
      }
    }
  }

  get subscriberCount() {
    return this.peers.size;
  }

  /**
   * Das Livebild hoechstens `maxHeight` hoch senden (kurze Bildseite), auch
   * wenn die Kamera gerade mehr liefert.
   *
   * Gebraucht auf der Senderseite: der Livestream geht mit 720p raus, eine
   * Videobotschaft soll aber 1080p haben -- und kommt von derselben Kamera.
   * Fuer die Aufnahme liefert die Kamera kurz 1080p, und der Encoder rechnet
   * das Livebild dabei auf 720p herunter (`scaleResolutionDownBy`). Geprueft
   * wird jede Sekunde: es gibt kein Ereignis, wenn eine Kamera ihre
   * Aufloesung wechselt, und eine Sekunde mehr Bild faellt niemandem auf.
   * Bildschirmfreigaben bekommen keine Grenze -- dort muss Schrift lesbar
   * bleiben.
   */
  limitVideo(maxHeight: number | null) {
    this.maxVideoHeight = maxHeight;
    if (this.fitTimer !== null) window.clearInterval(this.fitTimer);
    this.fitTimer = maxHeight ? window.setInterval(() => void this.fitVideo(), 1000) : null;
    void this.fitVideo(true);
  }

  private scaleNow(): number {
    const settings = this.stream?.getVideoTracks()[0]?.getSettings();
    if (!this.maxVideoHeight || !settings?.width || !settings.height) return 1;
    // Die kurze Seite zaehlt -- ein Handy hochkant meldet 720 x 1280.
    const short = Math.min(settings.width, settings.height);
    return short > this.maxVideoHeight + 8 ? short / this.maxVideoHeight : 1;
  }

  /** Grenze auf alle (oder eine neue) Verbindung anwenden, wenn sie sich geaendert hat. */
  private async fitVideo(force = false, only?: RTCPeerConnection) {
    const scale = this.scaleNow();
    if (!force && !only && Math.abs(scale - this.appliedScale) < 0.01) return;
    if (!only) this.appliedScale = scale;
    for (const pc of only ? [only] : [...this.peers.values()]) {
      for (const sender of pc.getSenders()) {
        if (sender.track?.kind !== 'video') continue;
        const params = sender.getParameters();
        const encoding = params.encodings?.[0];
        if (!encoding || (encoding.scaleResolutionDownBy ?? 1) === scale) continue;
        encoding.scaleResolutionDownBy = scale;
        await sender.setParameters(params).catch(() => undefined);
      }
    }
  }

  async addSubscriber(subscriberId: string) {
    if (!this.stream || this.peers.has(subscriberId)) return;
    const pc = new RTCPeerConnection(pcConfig(this.iceServers));
    this.peers.set(subscriberId, pc);

    // sendonly: wir schicken nur. Ein sendrecv-Transceiver (was addTrack
    // erzeugt) wuerde auf der Gegenseite unnoetig einen Empfangsweg aufbauen.
    for (const track of this.stream.getTracks()) {
      pc.addTransceiver(track, { direction: 'sendonly', streams: [this.stream] });
    }

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        this.signal(subscriberId, {
          lane: 'down',
          sourceId: this.sourceId,
          kind: 'ice',
          candidate: e.candidate.toJSON(),
        });
      }
    };
    pc.onconnectionstatechange = () => {
      this.onPeerState?.(subscriberId, pc.connectionState);
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        this.removeSubscriber(subscriberId);
      }
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    // Erst nach dem Angebot gibt es Encodings, an denen die Grenze haengen kann.
    if (this.maxVideoHeight) await this.fitVideo(false, pc);
    this.signal(subscriberId, {
      lane: 'down',
      sourceId: this.sourceId,
      kind: 'offer',
      sdp: { type: 'offer', sdp: pc.localDescription?.sdp },
    });
  }

  removeSubscriber(subscriberId: string) {
    const pc = this.peers.get(subscriberId);
    if (!pc) return;
    pc.onicecandidate = null;
    pc.onconnectionstatechange = null;
    pc.close();
    this.peers.delete(subscriberId);
    this.pendingIce.delete(subscriberId);
    this.queue.drop(subscriberId);
  }

  handleSignal(from: string, data: RtcPayload) {
    // Nicht unsere Quelle: das gehoert dem anderen Publisher dieses Tabs.
    if (data.lane !== 'up' || data.sourceId !== this.sourceId) return;
    void this.queue.run(from, async () => {
      const pc = this.peers.get(from);
      if (!pc) return;

      if (data.kind === 'answer') {
        await pc.setRemoteDescription({ type: 'answer', sdp: withoutRotationMark(data.sdp.sdp) });
        for (const candidate of this.pendingIce.get(from) ?? []) {
          await pc.addIceCandidate(candidate).catch(() => undefined);
        }
        this.pendingIce.delete(from);
        return;
      }

      if (data.kind === 'ice') {
        const candidate = data.candidate as RTCIceCandidateInit;
        // Vor der Antwort gepufferte Kandidaten gehen sonst verloren.
        if (!pc.remoteDescription) {
          const list = this.pendingIce.get(from) ?? [];
          list.push(candidate);
          this.pendingIce.set(from, list);
          return;
        }
        await pc.addIceCandidate(candidate).catch(() => undefined);
      }
    });
  }

  closeAll() {
    for (const id of [...this.peers.keys()]) this.removeSubscriber(id);
  }

  /** Endgueltig aufhoeren -- auch die Pruefung der Grenze. */
  dispose() {
    if (this.fitTimer !== null) window.clearInterval(this.fitTimer);
    this.fitTimer = null;
    this.closeAll();
  }
}

/**
 * Empfangsseite: abonniert eine oder mehrere Quellen. Adressiert wird ueber
 * die Verbindungs-ID des Publishers, die in der Quellenliste mitkommt.
 */
export class Subscriber {
  /**
   * Alles hier ist nach `sourceId` geschluesselt und nicht nach
   * Verbindungs-ID. Der Unterschied faellt genau dann auf, wenn *ein* Sender
   * zwei Quellen hat -- Regie mit Kamera und geteiltem Bildschirm: mit der
   * Verbindung als Schluessel ueberschreibt die zweite Aushandlung die erste,
   * und am Ende laeuft kein Bild.
   */
  private peers = new Map<string, RTCPeerConnection>();
  /** sourceId -> Verbindung des Publishers, fuer den Rueckweg. */
  private senderOf = new Map<string, string>();
  private pendingIce = new Map<string, RTCIceCandidateInit[]>();
  /**
   * Signale von einer Gegenstelle, die wir noch keiner Quelle zuordnen
   * koennen. Das passiert beim Wiederaufbau nach einem Reload des Senders:
   * dessen Angebot kann eintreffen, bevor die neue Verbindungs-ID durch den
   * React-Zustand gelaufen ist. Ohne Puffer waere das Angebot verloren und
   * das Bild kaeme erst nach dem naechsten Versuch zurueck.
   */
  private orphans = new Map<string, RtcPayload[]>();
  private queue = new SignalQueue();

  constructor(
    private signal: SignalFn,
    private iceServers: RTCIceServerLike[],
    private onTrack: (sourceId: string, stream: MediaStream) => void,
    private onGone: (sourceId: string) => void,
    private onState?: (sourceId: string, state: PeerState) => void,
  ) {}

  /** Merkt sich, welche Publisher-Verbindung zu welcher Quelle gehoert. */
  expect(sourceId: string, publisherConnectionId: string) {
    this.senderOf.set(sourceId, publisherConnectionId);
    const waiting = this.orphans.get(sourceId);
    if (!waiting) return;
    this.orphans.delete(sourceId);
    for (const data of waiting) this.handleSignal(publisherConnectionId, data);
  }

  forget(sourceId: string) {
    this.dropPeers(sourceId);
    this.onGone(sourceId);
  }

  /**
   * Derselbe Sender, neue Verbindung -- so sieht ein Reload auf der
   * Empfangsseite aus. Anders als `forget` wird der Stream *nicht*
   * zurueckgegeben: das <video> behaelt sein letztes Bild, bis das neue
   * Angebot da ist. Sonst blitzt bei jedem Reload des Senders Schwarz auf.
   */
  rebind(sourceId: string, publisherConnectionId: string) {
    this.dropPeers(sourceId);
    this.expect(sourceId, publisherConnectionId);
  }

  private dropPeers(sourceId: string) {
    this.senderOf.delete(sourceId);
    this.peers.get(sourceId)?.close();
    this.peers.delete(sourceId);
    this.pendingIce.delete(sourceId);
    this.orphans.delete(sourceId);
    this.queue.drop(sourceId);
  }

  handleSignal(from: string, data: RtcPayload) {
    if (data.lane !== 'down') return;
    const sourceId = data.sourceId;
    if (!this.senderOf.has(sourceId)) {
      const waiting = this.orphans.get(sourceId) ?? [];
      // Ein Angebot plus die ersten Kandidaten reichen; alles darueber waere
      // eine Quelle, die wir schlicht nicht erwarten.
      if (waiting.length < 40) waiting.push(data);
      this.orphans.set(sourceId, waiting);
      return;
    }

    void this.queue.run(sourceId, async () => {
      if (data.kind === 'offer') {
        this.peers.get(sourceId)?.close();
        const pc = new RTCPeerConnection(pcConfig(this.iceServers));
        this.peers.set(sourceId, pc);

        pc.onicecandidate = (e) => {
          if (e.candidate) {
            this.signal(from, { lane: 'up', sourceId, kind: 'ice', candidate: e.candidate.toJSON() });
          }
        };
        pc.ontrack = (e) => {
          /*
           * `ontrack` kommt pro Spur. Normalerweise haengen beide am selben
           * Stream. Fehlt dem Angebot die Zuordnung, bekaeme jede Spur ihren
           * eigenen -- und weil der zuletzt gemeldete gewinnt, liefe das Bild
           * dann ohne Ton. Deshalb in dem Fall alle Spuren der Verbindung
           * zusammen.
           */
          const tracks = pc.getReceivers().map((r) => r.track).filter((t) => t.readyState === 'live');
          this.onTrack(sourceId, e.streams[0] ?? new MediaStream(tracks.length ? tracks : [e.track]));
        };
        pc.onconnectionstatechange = () => {
          /*
           * Bei 'failed' wird der Stream *nicht* zurueckgegeben. Das
           * <video>-Element behaelt dann sein letztes Bild stehen, statt auf
           * Schwarz zu springen -- und genau das ist beim Reload des Senders
           * das gewuenschte Verhalten: kurz eingefroren statt abgebrochen.
           * Endgueltig raeumt erst `forget` auf, wenn die Quelle wirklich weg
           * ist.
           */
          this.onState?.(sourceId, pc.connectionState);
        };

        await pc.setRemoteDescription({ type: 'offer', sdp: withoutRotationMark(data.sdp.sdp) });
        for (const candidate of this.pendingIce.get(sourceId) ?? []) {
          await pc.addIceCandidate(candidate).catch(() => undefined);
        }
        this.pendingIce.delete(sourceId);

        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.signal(from, {
          lane: 'up',
          sourceId,
          kind: 'answer',
          sdp: { type: 'answer', sdp: pc.localDescription?.sdp },
        });
        return;
      }

      if (data.kind === 'ice') {
        const pc = this.peers.get(sourceId);
        const candidate = data.candidate as RTCIceCandidateInit;
        if (!pc?.remoteDescription) {
          const list = this.pendingIce.get(sourceId) ?? [];
          list.push(candidate);
          this.pendingIce.set(sourceId, list);
          return;
        }
        await pc.addIceCandidate(candidate).catch(() => undefined);
      }
    });
  }

  closeAll() {
    for (const pc of this.peers.values()) pc.close();
    this.peers.clear();
    this.senderOf.clear();
    this.pendingIce.clear();
    this.orphans.clear();
  }
}

/** Welche Geraete genommen werden sollen. Leer = der Browser entscheidet. */
export interface DeviceChoice {
  camera?: string;
  mic?: string;
}

/**
 * `exact` und nicht `ideal`: eine Geraetewahl, die stillschweigend ein anderes
 * Mikrofon nimmt, ist schlimmer als eine, die scheitert -- dann sucht die
 * Regie den Fehler im Kabel.
 */
const exact = (deviceId: string | undefined) => (deviceId ? { deviceId: { exact: deviceId } } : {});

/**
 * Was bei Kamera oder Mikro schiefging -- in Worten, die man am Handy
 * versteht. Die Meldungen der Browser („Could not start video source") sagen
 * dort niemandem etwas.
 */
export function mediaErrorText(err: unknown): string {
  const name = (err as { name?: string })?.name ?? '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Kamera oder Mikrofon sind nicht erlaubt — bitte in den Einstellungen des Browsers bzw. des Handys freigeben.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'Keine passende Kamera gefunden.';
  if (name === 'NotReadableError' || name === 'AbortError') {
    return 'Kamera oder Mikrofon sind gerade von einer anderen App belegt — etwa einer Streaming-App, einem Anruf oder einer Bildschirmübertragung. Schließ sie und öffne die Kamera erneut.';
  }
  return err instanceof Error && err.message ? err.message : 'Kamera bzw. Mikrofon ließen sich nicht öffnen.';
}

/**
 * Kamera + Mikro des Geraets. Braucht einen Secure Context (HTTPS/localhost).
 * `withMic: false` holt nur das Bild -- der Ausweg, wenn das Mikrofon belegt
 * oder nicht erlaubt ist.
 */
export async function openCamera(
  facing: 'user' | 'environment',
  choice: DeviceChoice = {},
  withMic = true,
): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error(
      'Dieser Browser gibt keine Kamera frei. Auf dem Handy braucht die Seite HTTPS — siehe README (npm run cert).',
    );
  }
  return navigator.mediaDevices.getUserMedia({
    video: {
      // Ist ein Geraet gewaehlt, hat es Vorrang vor der Blickrichtung: die
      // gibt es nur auf dem Handy, die Wahl nur am Rechner.
      ...(choice.camera ? exact(choice.camera) : { facingMode: { ideal: facing } }),
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 30 },
    },
    audio: withMic ? { ...exact(choice.mic), echoCancellation: true, noiseSuppression: true } : false,
  });
}

/**
 * Nur das Mikrofon.
 *
 * Gebraucht fuer die Bildschirmuebertragung: `getDisplayMedia` gibt den Ton
 * des geteilten Inhalts heraus, das Mikrofon aber nie. Die Echo-Unterdrueckung
 * bleibt an -- sie haelt den Bildschirmton aus dem Mikrofon heraus, wenn er
 * ueber die Lautsprecher mitlaeuft.
 */
export async function openMic(deviceId?: string): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Dieser Browser gibt kein Mikrofon frei.');
  }
  return navigator.mediaDevices.getUserMedia({
    audio: { ...exact(deviceId), echoCancellation: true, noiseSuppression: true },
  });
}

/**
 * Kann dieser Browser den Bildschirm teilen? Auf dem iPhone erst ab iOS 27 --
 * in Safari wie in Chrome, die dort beide auf Apples WebKit laufen.
 */
export const canShareScreen = (): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function';

/** Bildschirm bzw. Fenster teilen. */
export async function openScreen(): Promise<MediaStream> {
  if (!canShareScreen()) {
    throw new Error('Bildschirmübertragung geht in diesem Browser nicht — am Laptop mit Chrome, Edge oder Safari.');
  }
  try {
    return await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: { ideal: 30 } },
      audio: true,
    });
  } catch (err) {
    // Abgelehnt oder abgebrochen: das war eine Entscheidung, kein Grund fuer einen zweiten Dialog.
    if (err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'AbortError')) throw err;
    /*
     * Das iPhone teilt den Bildschirm (ab iOS 27), kennt aber nicht jede
     * Vorgabe -- Ton des geteilten Inhalts und Bildrate allen voran. Dann
     * eben nur das Bild; das Mikro kommt ohnehin getrennt dazu.
     */
    return navigator.mediaDevices.getDisplayMedia({ video: true });
  }
}

export const stopStream = (stream: MediaStream | null | undefined) =>
  stream?.getTracks().forEach((t) => t.stop());
